import { useCallback } from 'react'
import { useMetroMapStore } from './useMetroMapStore'
import { useRouteSelection } from './useRouteSelection'
import { useRoutingAdapter } from './useRoutingAdapter'
import { useRoutingTelemetry } from './useRoutingTelemetry'

/**
 * `route_variant_tapped` for a RouteCard variant chip tap — the one emit
 * point shared by the phone overlay (`MobileRouteCardOverlay`) and the
 * desktop side panel (`StationCardOrDetailsRouter`), so the two surfaces
 * cannot drift. Until 2026-10-28 only the side panel reported it, and the
 * site's most-tapped route control (a quarter of the route-page clicks,
 * nearly all on phones) reached Amplitude as zero.
 *
 * Reports only a tap on a result set with both ends picked; the initial
 * auto-select is not a tap and never goes through here.
 */
export function useRouteVariantTappedReporter(): (variantIndex: number) => void {
  const telemetry = useRoutingTelemetry()
  const routeSelection = useRouteSelection()
  const adapter = useRoutingAdapter()
  const store = useMetroMapStore()
  return useCallback(
    (variantIndex: number) => {
      const routeStatus = store.getState().routeStatus
      const { fromStation, toStation } = routeSelection
      if (routeStatus.kind !== 'results' || fromStation === null || toStation === null) return
      telemetry({
        kind: 'variantTapped',
        fromStation: fromStation.id,
        toStation: toStation.id,
        variantIndex,
        variantTotal: routeStatus.routes.length,
        routeSource: adapter?.routeSource(variantIndex) ?? 'offline',
      })
    },
    [telemetry, routeSelection, adapter, store],
  )
}
